import * as moment from 'moment';
import { z } from 'zod';

import type APIAddress from '~/types/APIAddress';
import type LabelFormat from '~/types/LabelFormat';
import type ParcelTemplate from '~/types/ParcelTemplate';

import { currencies, type Currency } from '~/constants/currency';
import { DistanceUnit, distanceUnitMap } from '~/constants/distance';
import { WeightUnit, weightUnitMap } from '~/constants/weight';
import {
  type JSONPromo,
  type Promo,
} from '~/modules/billing/components/PromoCode/types/promo.type';
import { promoFromJSON } from '~/modules/billing/components/PromoCode/utils/promo.util';
import * as apiAddress from '~/types/APIAddress';
import * as parcelTemplate from '~/types/ParcelTemplate';

const BILLING_BLOCKED_STATUS_SCHEMA = z.enum(['BLOCKED', 'FAILED_PAYMENT', 'UNBLOCKED']);
export const BILLING_BLOCKED_STATUS_ENUM = BILLING_BLOCKED_STATUS_SCHEMA.enum;
export type BillingBlockedStatuses = z.infer<typeof BILLING_BLOCKED_STATUS_SCHEMA>;

export const BUSINESS_TYPE_OPTIONS = [
  {
    caption: 'api.businessTypeModal.directShipperCaption',
    text: 'api.businessTypeModal.directShipper',
    value: 'DIRECT_SHIPPER',
  },
  {
    caption: 'api.businessTypeModal.defaultCaption',
    text: 'api.businessTypeModal.eCommercePlatform',
    value: 'ECOMMERCE_PLATFORM',
  },
  {
    caption: 'api.businessTypeModal.defaultCaption',
    text: 'api.businessTypeModal.marketplace',
    value: 'MARKETPLACE',
  },
  {
    caption: 'api.businessTypeModal.defaultCaption',
    text: 'api.businessTypeModal.wms',
    value: 'WMS_IMS_OMS',
  },
  {
    caption: 'api.businessTypeModal.3plCaption',
    text: 'api.businessTypeModal.3pl',
    value: '3PL',
  },
] as const;

export type BusinessType = (typeof BUSINESS_TYPE_OPTIONS)[number]['value'];

export type JSONUser = {
  address: apiAddress.JSONAPIAddress;
  address_return: apiAddress.JSONAPIAddress;
  api_key_generation_enabled?: boolean;
  are_order_tags_visible: boolean;
  available_credit: {
    amount: string;
    currency: Currency['iso'];
  }[];
  billing_blocked_status?: BillingBlockedStatuses;
  blocked_billing: boolean;
  business_type: BusinessType;
  currency_iso: string;
  current_promo: Maybe<JSONPromo>;
  date_joined: string;
  default_auto_create_return_label: boolean;
  default_distance_unit: string;
  default_label_filetype: Maybe<string>;
  default_mass_unit: string;
  default_outbound_account: Maybe<string>;
  default_parcel: parcelTemplate.JSONParcelTemplate;
  default_provider: Maybe<string>;
  default_return_account: Maybe<string>;
  default_return_provider: Maybe<string>;
  default_return_service_level: Maybe<string>;
  default_service_level: Maybe<string>;
  email: string;
  federated_emails: string[];
  first_name: string;
  fraud_status: string; // This is fraud_status_id. Intentionally vaguely named to stop attackers from tampering.
  group_jwt: string;
  group_name: string;
  has_failed_payment: boolean;
  has_paid_invoice: boolean;
  has_purchased_label: boolean;
  id: number;
  insurance_rate: number;
  insurance_rate_domestic?: number;
  insurance_rate_international?: number;
  invoice_adjustments: string[];
  is_account_owner: boolean;
  is_active: boolean;
  is_idv_required: boolean;
  is_password_login_allowed: boolean;
  is_prepaid: boolean;
  is_setup: boolean;
  is_third_party_insurance_disabled: boolean;
  last_name: string;
  lifetime_vol_segment_max: number;
  lifetime_vol_segment_min: number;
  login_user_id: number;
  object_id: string;
  payment_type: string;
  requires_email_verification: Maybe<boolean>;
  revenue_tier_name: string;
  savings_carrier_name: string;
  savings_monthly_amt: string;
  signed_up_at: number;
  stripe_authorized: boolean;
  stripe_publishable_key: string;
  username: string;
};

export type User = {
  address: Maybe<APIAddress>;
  addressReturn: Maybe<APIAddress>;
  apiKeyGenerationEnabled?: boolean;
  areOrderTagsVisible: boolean;
  availableCredit: {
    amount: string;
    currency: Currency['iso'];
  }[];
  billingBlockedStatus?: BillingBlockedStatuses;
  blockedBilling: boolean;
  businessType: BusinessType;
  currencyIso: Currency;
  currentPromo: Maybe<Promo>;
  dateJoined: moment.Moment;
  default_outbound_account?: null | string;
  defaultAutoCreateReturnLabel: boolean;
  defaultDistanceUnit: DistanceUnit;
  defaultLabelFiletype: Maybe<LabelFormat>;
  defaultMassUnit: WeightUnit;
  defaultOutboundAccount: Maybe<string>;
  defaultParcel: Maybe<ParcelTemplate>;
  defaultProvider: Maybe<string>;
  defaultReturnAccount?: null | string;
  defaultReturnProvider: Maybe<string>;
  defaultReturnServiceLevel: Maybe<string>;
  defaultServiceLevel: Maybe<string>;
  email: string;
  federatedEmails: string[];
  firstName: string;
  fraudStatus: string;
  groupName: string; // This is fraud_status_id. Intentionally vaguely named to stop attackers from tampering.
  hasFailedPayment: boolean;
  hasPaidInvoice: boolean;
  hasPurchasedLabel: boolean;
  id: number;
  insuranceRate: number;
  insuranceRateDomestic?: number;
  insuranceRateInternational?: number;
  invoiceAdjustments: string[];
  isAccountOwner: boolean;
  isActive: boolean;
  isIDVRequired: boolean;
  isPasswordLoginAllowed: boolean;
  isPrepaidBilling: boolean;
  isSetup: boolean;
  isThirdPartyInsuranceDisabled: boolean;
  lastName: string;
  lifetimeVolSegmentMax: number;
  lifetimeVolSegmentMin: number;
  loginUserId: number;
  objectId: string;
  paymentType: string;
  requiresEmailVerification: boolean;
  revenueTierName: string;
  savingsCarrierName: string;
  savingsMonthlyAmount: number;
  signedUpAt: number;
  stripeAuthorized: boolean;
  stripePublishableKey: string;
  username: string;
};

export type UserConfigDefaults = {
  defaultAutoCreateReturnLabel: boolean;
  defaultCurrency: Currency;
  defaultDistanceUnit: DistanceUnit;
  defaultMassUnit: WeightUnit;
  defaultParcel: ParcelTemplate;
};

export type UserDefaultServiceLevel = {
  accountId: Maybe<string | undefined>;
  provider: string;
  token: string;
};

export const getFullName = (user: User) => `${user.firstName} ${user.lastName}`;

export const hasValidBilling = (user: User) => {
  if (!user.isActive) return false;

  return user.stripeAuthorized || (user.paymentType !== null && user.paymentType !== '');
};

export const isHighFraudRisk = (user: User) => user.fraudStatus === 'HIGH_RISK';

export const fromJSON = (jsonUser: JSONUser): User => ({
  address: apiAddress.fromJSON(jsonUser.address),
  addressReturn: apiAddress.fromJSON(jsonUser.address_return),
  apiKeyGenerationEnabled: jsonUser.api_key_generation_enabled,
  areOrderTagsVisible: jsonUser.are_order_tags_visible,
  availableCredit: jsonUser.available_credit,
  billingBlockedStatus: jsonUser.billing_blocked_status,
  blockedBilling: jsonUser.blocked_billing,
  businessType: jsonUser.business_type,
  currencyIso: currencies[jsonUser.currency_iso.toUpperCase()] || currencies.USD,
  currentPromo: promoFromJSON(jsonUser.current_promo),
  dateJoined: moment(jsonUser.date_joined),
  defaultAutoCreateReturnLabel: jsonUser.default_auto_create_return_label,
  defaultDistanceUnit: distanceUnitMap[jsonUser.default_distance_unit] || DistanceUnit.IN,
  defaultLabelFiletype: <LabelFormat>jsonUser.default_label_filetype,
  defaultMassUnit: weightUnitMap[jsonUser.default_mass_unit] || WeightUnit.LB,
  defaultOutboundAccount: jsonUser.default_outbound_account,
  defaultParcel: jsonUser.default_parcel ? parcelTemplate.fromJSON(jsonUser.default_parcel) : null,
  defaultProvider: jsonUser.default_provider,
  defaultReturnAccount: jsonUser.default_return_account,
  defaultReturnProvider: jsonUser.default_return_provider,
  defaultReturnServiceLevel: jsonUser.default_return_service_level,
  defaultServiceLevel: jsonUser.default_service_level,
  email: jsonUser.email,
  federatedEmails: jsonUser.federated_emails,
  firstName: jsonUser.first_name,
  fraudStatus: jsonUser.fraud_status,
  groupName: jsonUser.group_name,
  hasFailedPayment: jsonUser.has_failed_payment || false,
  hasPaidInvoice: jsonUser.has_paid_invoice,
  hasPurchasedLabel: jsonUser.has_purchased_label,
  id: jsonUser.id,
  insuranceRate: jsonUser.insurance_rate,
  insuranceRateDomestic: jsonUser.insurance_rate_domestic,
  insuranceRateInternational: jsonUser.insurance_rate_international,
  invoiceAdjustments: jsonUser.invoice_adjustments,
  isAccountOwner: jsonUser.is_account_owner,
  isActive: jsonUser.is_active,
  isIDVRequired: !!jsonUser.is_idv_required,
  isPasswordLoginAllowed: jsonUser.is_password_login_allowed,
  isPrepaidBilling: jsonUser.is_prepaid,
  isSetup: jsonUser.is_setup,
  isThirdPartyInsuranceDisabled: !!jsonUser.is_third_party_insurance_disabled,
  lastName: jsonUser.last_name,
  lifetimeVolSegmentMax: jsonUser.lifetime_vol_segment_max,
  lifetimeVolSegmentMin: jsonUser.lifetime_vol_segment_min,
  loginUserId: jsonUser.login_user_id,
  objectId: jsonUser.object_id,
  paymentType: jsonUser.payment_type,
  requiresEmailVerification: !!jsonUser.requires_email_verification,
  revenueTierName: jsonUser.revenue_tier_name || '5',
  savingsCarrierName: jsonUser.savings_carrier_name,
  savingsMonthlyAmount: parseFloat(jsonUser.savings_monthly_amt),
  signedUpAt: jsonUser.signed_up_at,
  stripeAuthorized: jsonUser.stripe_authorized,
  stripePublishableKey: jsonUser.stripe_publishable_key,
  username: jsonUser.username,
});

export type ProfileUser = {
  email: string;
  federatedEmails: string[];
  firstName: string;
  isAccountOwner: boolean;
  isPasswordLoginAllowed: boolean;
  lastName: string;
  loginUserId: number;
  name: string;
  newPassword: string;
  newPasswordConfirmation: string;
  password: string;
};

export type LoginUser = {
  email: string;
  firstName: string;
  id: number;
  isActive: boolean;
  isEmailRecipient: boolean;
  lastName: string;
  username: string;
};

export default User;
